import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Global Role Guard Controller
// Protects against accidental assignment of global (system-wide) roles
// Global roles are those in ADMINS + VIEWERS (roles with no resource_type)
//
// Features:
// 1. Shows warning when a global role is selected
// 2. Intercepts form submission and shows confirmation dialog
// 3. Works with dynamically added nested fields
//
// Usage:
//   .card[data-controller="nested-fields global-role-guard"
//         data-global-role-guard-warning-text-value="..."
//         data-global-role-guard-confirm-title-value="..."
//         data-global-role-guard-confirm-text-value="..."]
//
export default class extends Controller {
  static values = {
    warningText: { type: String, default: "שים לב: תפקיד זה מעניק הרשאות גלובליות במערכת" },
    confirmTitle: { type: String, default: "אישור תפקיד גלובלי" },
    confirmText: { type: String, default: "למשתמש זה תפקיד עם הרשאות גלובליות במערכת.<br>אנא אשר/י להמשך." },
    confirmButtonText: { type: String, default: "אישור" },
    cancelButtonText: { type: String, default: "ביטול" }
  }

  connect() {
    logger.log("[GlobalRoleGuard] Controller connected")

    // Find the parent form
    this.form = this.element.closest('form')
    if (!this.form) {
      logger.error("[GlobalRoleGuard] No parent form found")
      return
    }

    // Store initial role values to detect changes
    this.initialRoleValues = this.captureRoleValues()
    logger.log("[GlobalRoleGuard] Initial role values:", this.initialRoleValues)

    // Bind form submit handler
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.form.addEventListener('submit', this.boundHandleSubmit)

    // Initial check for any existing global roles
    this.updateWarnings()

    // Listen for nested-fields events (when new roles are added/removed)
    this.element.addEventListener('nested-fields:added', this.handleNestedFieldChanged.bind(this))
    this.element.addEventListener('nested-fields:removed', this.handleNestedFieldChanged.bind(this))
  }

  // Capture current role values (for change detection)
  captureRoleValues() {
    const values = new Map()
    const selectors = this.element.querySelectorAll('.job-type-selector')

    selectors.forEach(selector => {
      if (selector.closest('template')) return

      const row = selector.closest('[data-nested-fields-target="item"]') || selector.closest('tr')
      if (!row) return

      // Use the row's id field to identify persisted roles
      const idField = row.querySelector('input[name*="[id]"]')
      const roleId = idField?.value || null

      // For new roles (no id), use a unique key based on selector
      const key = roleId || `new_${selector.id || Math.random()}`

      values.set(key, {
        value: selector.value,
        isGlobal: this.isGlobalRole(selector),
        isNew: !roleId
      })
    })

    return values
  }

  disconnect() {
    if (this.form && this.boundHandleSubmit) {
      this.form.removeEventListener('submit', this.boundHandleSubmit)
    }
  }

  // Called when a role type selector changes (via data-action)
  roleTypeChanged(event) {
    const selector = event.target
    this.updateSingleWarning(selector)
  }

  // Handle when a nested field is added or removed
  handleNestedFieldChanged(event) {
    logger.log("[GlobalRoleGuard] Nested field changed")
    // Small delay to ensure DOM is updated
    setTimeout(() => this.updateWarnings(), 50)
  }

  // Check if a role is a global role (no resource_type = ADMINS + VIEWERS)
  isGlobalRole(selector) {
    const selectedOption = selector.selectedOptions[0]
    if (!selectedOption || !selectedOption.value) return false

    // Global roles have empty or undefined resource_type
    const resourceType = selectedOption.dataset.resourceType
    return !resourceType || resourceType === ''
  }

  // Update warning visibility for a single selector
  updateSingleWarning(selector) {
    const row = selector.closest('[data-nested-fields-target="item"]') || selector.closest('tr')
    if (!row) return

    const isGlobal = this.isGlobalRole(selector)
    const cell = selector.closest('td') || selector.parentElement

    // Find or create warning element for this row
    let warningEl = cell.querySelector('.global-role-warning')

    if (isGlobal) {
      // Show warning
      if (!warningEl) {
        warningEl = this.createWarningElement()
        cell.appendChild(warningEl)
      }
      warningEl.classList.remove('d-none')
      logger.log("[GlobalRoleGuard] Global role selected:", selector.selectedOptions[0].textContent)
    } else if (warningEl) {
      // Hide warning
      warningEl.classList.add('d-none')
    }
  }

  // Update all warnings
  updateWarnings() {
    // Find all role type selectors (not in templates)
    const selectors = this.element.querySelectorAll('.job-type-selector')
    selectors.forEach(selector => {
      // Skip selectors inside template elements
      if (selector.closest('template')) return
      this.updateSingleWarning(selector)
    })
  }

  // Create a warning element
  createWarningElement() {
    const warningEl = document.createElement('div')
    warningEl.className = 'alert alert-warning global-role-warning py-1 px-2 mt-1 mb-0 small'
    warningEl.innerHTML = `<i class="fa fa-exclamation-triangle me-1"></i>${this.warningTextValue}`
    return warningEl
  }

  // Get global roles that were added or changed (not existing unchanged ones)
  getChangedGlobalRoles() {
    const changedGlobalRoles = []
    const selectors = this.element.querySelectorAll('.job-type-selector')

    selectors.forEach(selector => {
      // Skip selectors inside templates
      if (selector.closest('template')) return

      // Skip if row is hidden (removed nested field)
      const row = selector.closest('[data-nested-fields-target="item"]') || selector.closest('tr')
      if (row && row.style.display === 'none') return

      // Check if _destroy field is set (role marked for deletion)
      const destroyField = row?.querySelector('input[name*="[_destroy]"]')
      if (destroyField && (destroyField.value === '1' || destroyField.value === 'true')) return

      // Only proceed if this is a global role
      if (!this.isGlobalRole(selector)) return

      // Check if this role was added or changed
      const idField = row?.querySelector('input[name*="[id]"]')
      const roleId = idField?.value || null

      if (!roleId) {
        // New role - always needs confirmation if it's global
        changedGlobalRoles.push({
          name: selector.selectedOptions[0].textContent.trim(),
          value: selector.selectedOptions[0].value,
          isNew: true
        })
      } else {
        // Existing role - check if value changed
        const initial = this.initialRoleValues.get(roleId)
        if (!initial || initial.value !== selector.value) {
          // Role was changed to a (different) global role
          changedGlobalRoles.push({
            name: selector.selectedOptions[0].textContent.trim(),
            value: selector.selectedOptions[0].value,
            isNew: false,
            wasChanged: true
          })
        }
        // If value is same as initial, don't add - no confirmation needed
      }
    })

    return changedGlobalRoles
  }

  // Handle form submission
  async handleSubmit(event) {
    const changedGlobalRoles = this.getChangedGlobalRoles()

    if (changedGlobalRoles.length === 0) {
      // No new or changed global roles, allow form to submit normally
      return
    }

    // Prevent form submission until user confirms
    event.preventDefault()
    event.stopPropagation()

    logger.log("[GlobalRoleGuard] Form submission intercepted, changed global roles:", changedGlobalRoles)

    // Build confirmation message with role names
    const roleNames = changedGlobalRoles.map(r => r.name).join(', ')
    const confirmHtml = changedGlobalRoles.length === 1
      ? `${this.confirmTextValue}<br><br><strong>תפקיד:</strong> ${roleNames}`
      : `${this.confirmTextValue}<br><br><strong>תפקידים:</strong> ${roleNames}`

    // Show SweetAlert2 confirmation
    const result = await window.Swal.fire({
      title: this.confirmTitleValue,
      html: confirmHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `<i class="fa fa-check"></i> ${this.confirmButtonTextValue}`,
      cancelButtonText: `<i class="fa fa-times"></i> ${this.cancelButtonTextValue}`,
      customClass: {
        confirmButton: 'btn btn-danger me-2',
        cancelButton: 'btn btn-secondary ms-2',
        actions: 'd-flex gap-2 justify-content-center'
      },
      buttonsStyling: false,
      reverseButtons: true  // RTL layout
    })

    if (result.isConfirmed) {
      logger.log("[GlobalRoleGuard] User confirmed, submitting form")

      // Remove our handler to prevent infinite loop
      this.form.removeEventListener('submit', this.boundHandleSubmit)

      // Re-submit the form using the original submitter (button that was clicked)
      // This ensures the correct button name is sent (e.g., user[invite], user[become])
      this.form.requestSubmit(event.submitter)
    } else {
      logger.log("[GlobalRoleGuard] User cancelled")
    }
  }
}
